/**
 * The orphan review panel — ROADMAP 7.8.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS NOT HERE WHEN THERE IS NOTHING TO REVIEW
 *
 * Which is almost always. A permanently-present "Reconcile" affordance is
 * what trains a trader to ignore it; this appears only when the broker has
 * actually stopped reporting something we hold, and then it is the loudest
 * thing on the screen.
 *
 * EVERY PROPOSAL IS LABELLED WITH HOW MUCH WE KNOW
 *
 *   a plain price   one leg held the contract, so this fill IS this leg's
 *   SPLIT           more than one leg shared it; the split is OUR choice and
 *                   the trader is the one who can check it
 *   no price        nothing matched. The field is empty and must be typed,
 *                   which records it as ADJUSTED forever
 *
 * Collapsing those three into one number is the whole failure this screen
 * exists to avoid: a guess that looks like a reading.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useMemo, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { contractExpired } from '@apna/shared'
import type { LegDecision, OrphanReview, ReviewLeg, ReviewTrade } from './useOrphanReview'

/** What the user has done to one row. `undefined` means not yet touched. */
type RowChoice =
  | { kind: 'CONFIRM' }
  /** The price box was typed in. Held as the raw string so a half-typed
   *  "8." is not snapped back to 8 under the cursor. */
  | { kind: 'EDIT'; raw: string }
  | { kind: 'DEFER' }

/**
 * What to call the legs in this panel.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT CANNOT JUST SAY "THE BROKER STOPPED REPORTING"
 *
 * That was the only reason a leg could be here, and it is still the common
 * one — but a leg is also flagged when its CONTRACT EXPIRED, and on a paper
 * trade that is the only possibility, because there is no broker behind it.
 * Printing the broker sentence over an expired paper leg sends the trader to
 * check an account that was never involved.
 *
 * The reason is derived from the leg's own expiry rather than sent as a word,
 * for the same reason `reviewReason` does it in the terminal: the date is the
 * fact, and a second copy of the conclusion is the thing that disagrees with
 * it. Expiry wins when both are true, because on a live trade both usually
 * are — the broker drops an expired contract from its book — and the contract
 * dying is the cause rather than how we found out.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function headline(trades: readonly ReviewTrade[]): string {
  const legs = trades.flatMap((t) => t.legs)
  const expired = legs.filter((l) => contractExpired(l.expiry)).length
  const quiet = legs.length - expired

  const n = (count: number) => `${count} position${count === 1 ? '' : 's'}`

  if (expired === 0) return `${n(quiet)} the broker stopped reporting`
  if (quiet === 0) return `${n(expired)} on contracts that have expired`
  return `${n(expired)} on expired contracts, ${n(quiet)} the broker stopped reporting`
}

export function ReviewPanel({ review }: { review: OrphanReview }): React.JSX.Element | null {
  const [choices, setChoices] = useState<Record<string, RowChoice>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const payload = review.state.kind === 'READY' ? review.state.review : null

  /*
   * A leg with no proposal defaults to EDIT with an empty box, because there
   * is nothing to confirm. One with a proposal defaults to nothing at all —
   * the user must say so, and a row they never looked at is sent as DEFER
   * rather than silently accepted.
   */
  const choiceFor = (leg: ReviewLeg): RowChoice =>
    choices[leg.tradeLegId] ?? (leg.proposedPrice === null ? { kind: 'EDIT', raw: '' } : { kind: 'DEFER' })

  const decisions = useMemo((): LegDecision[] => {
    if (!payload) return []
    return payload.trades.flatMap((t) =>
      t.legs.map((leg): LegDecision => {
        const c = choiceFor(leg)
        if (c.kind === 'CONFIRM' && leg.proposedPrice !== null) {
          return {
            tradeLegId: leg.tradeLegId,
            action: 'CONFIRM',
            price: leg.proposedPrice,
            ...(leg.proposedExitAt === null ? {} : { exitedAt: leg.proposedExitAt }),
          }
        }
        if (c.kind === 'EDIT') {
          const price = Number(c.raw)
          // An unparseable or empty box is NOT a decision. Sent as DEFER so
          // the leg stays flagged rather than being priced at zero.
          if (!Number.isFinite(price) || price <= 0) {
            return { tradeLegId: leg.tradeLegId, action: 'DEFER' }
          }
          return { tradeLegId: leg.tradeLegId, action: 'EDIT', price }
        }
        return { tradeLegId: leg.tradeLegId, action: 'DEFER' }
      }),
    )
    // `choices` is the dependency that matters; `payload` changes only on a
    // refetch, which also resets what is on screen.
  }, [payload, choices])

  const resolving = decisions.filter((d) => d.action !== 'DEFER').length

  if (review.state.kind === 'ERROR') {
    return (
      <Shell tone="muted">
        <p className="text-[11px] text-muted-foreground" data-testid="review-error">
          The review could not be read: {review.state.why}
        </p>
      </Shell>
    )
  }
  if (!payload || payload.trades.length === 0) return null

  const apply = async (): Promise<void> => {
    setBusy(true)
    setError(null)
    setDone(null)
    try {
      const r = await review.submit(decisions)
      setChoices({})
      setDone(
        `${r.resolved} leg(s) recorded, ${r.deferred} left for later` +
          (r.tradesClosed > 0 ? `, ${r.tradesClosed} trade(s) closed` : '') +
          '.',
      )
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'The review could not be applied.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Shell tone="warning">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-1.5 text-[12px] font-extrabold text-warning">
            <AlertTriangle size={13} />
            {headline(payload.trades)}
          </h2>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            Nothing has been changed. These count in no total until you confirm how they closed.
          </p>
        </div>
        <button
          type="button"
          data-testid="review-apply"
          onClick={() => void apply()}
          disabled={busy || resolving === 0}
          className="h-8 shrink-0 rounded-lg bg-warning/20 px-4 text-[11px] font-extrabold text-warning transition-all hover:bg-warning hover:text-black disabled:opacity-40"
        >
          {busy ? 'RECORDING…' : `RECORD ${resolving}`}
        </button>
      </div>

      {payload.unreadable !== null && (
        <p className="mt-2 text-[10px] text-destructive" data-testid="review-unreadable">
          The broker&apos;s trade book could not be read ({payload.unreadable}), so no prices are
          proposed. You can still record them by hand, or come back when it answers.
        </p>
      )}
      {payload.unclaimed > 0 && (
        <p className="mt-2 text-[10px] text-muted-foreground" data-testid="review-unclaimed">
          {payload.unclaimed} unit(s) were closed in these contracts that no trade of ours
          accounts for — likely a position opened outside this software.
        </p>
      )}

      <table className="num mt-2.5 w-full text-[11px]">
        <tbody>
          {payload.trades.map((t) =>
            t.legs.map((leg) => {
              const c = choiceFor(leg)
              const set = (next: RowChoice): void =>
                setChoices((prev) => ({ ...prev, [leg.tradeLegId]: next }))

              return (
                <tr
                  key={leg.tradeLegId}
                  className="border-b border-border/40"
                  data-testid={`review-row-${leg.tradeLegId}`}
                >
                  <td className="py-1.5">
                    <span className={leg.ourQty < 0 ? 'text-destructive' : 'text-success'}>
                      {leg.ourQty < 0 ? 'SELL' : 'BUY'}
                    </span>
                  </td>
                  <td>{leg.label}</td>
                  <td className="text-right text-muted-foreground">
                    {leg.orphanedQty.toLocaleString('en-IN')}
                  </td>

                  <td className="py-1 text-right">
                    {c.kind === 'EDIT' ? (
                      <input
                        type="number"
                        step="0.05"
                        min="0"
                        inputMode="decimal"
                        value={c.raw}
                        placeholder={leg.proposedPrice === null ? 'price' : leg.proposedPrice.toFixed(2)}
                        onChange={(e) => set({ kind: 'EDIT', raw: e.target.value })}
                        data-testid={`review-price-${leg.tradeLegId}`}
                        className="h-7 w-24 rounded-md border border-warning/40 bg-background px-2 text-right text-[11px] outline-none focus:border-warning"
                      />
                    ) : (
                      <span className={cn(leg.proposedPrice === null && 'text-muted-foreground')}>
                        {leg.proposedPrice === null ? '—' : leg.proposedPrice.toFixed(2)}
                      </span>
                    )}
                  </td>

                  <td className="pl-2 text-left">
                    {/* The provenance of the number to its left. */}
                    {c.kind === 'EDIT' ? (
                      <Tag tone="destructive" title="A typed price is recorded as ADJUSTED and stays marked.">
                        ADJUSTED
                      </Tag>
                    ) : leg.allocated ? (
                      <Tag tone="warning" title="More than one leg held this contract. Which fill closed which leg is our choice — please check it.">
                        SPLIT
                      </Tag>
                    ) : null}
                    {leg.unmatched > 0 && (
                      <Tag tone="muted" title="Units no execution in the broker's book covers.">
                        {leg.unmatched} UNMATCHED
                      </Tag>
                    )}
                  </td>

                  <td className="py-1 pl-2 text-right">
                    <div className="inline-flex gap-1">
                      <Choice
                        testId={`review-confirm-${leg.tradeLegId}`}
                        on={c.kind === 'CONFIRM'}
                        disabled={leg.proposedPrice === null}
                        title={
                          leg.proposedPrice === null
                            ? 'Nothing matched, so there is nothing to confirm. Type the price instead.'
                            : "Record the broker's own price."
                        }
                        onClick={() => set({ kind: 'CONFIRM' })}
                      >
                        CONFIRM
                      </Choice>
                      <Choice
                        testId={`review-edit-${leg.tradeLegId}`}
                        on={c.kind === 'EDIT'}
                        title="Type the price yourself. It is recorded as ADJUSTED forever."
                        onClick={() =>
                          set({ kind: 'EDIT', raw: leg.proposedPrice === null ? '' : String(leg.proposedPrice) })
                        }
                      >
                        EDIT
                      </Choice>
                      <Choice
                        testId={`review-defer-${leg.tradeLegId}`}
                        on={c.kind === 'DEFER'}
                        title="Leave it flagged. Nothing is written and it stays out of every total."
                        onClick={() => set({ kind: 'DEFER' })}
                      >
                        LATER
                      </Choice>
                    </div>
                  </td>
                </tr>
              )
            }),
          )}
        </tbody>
      </table>

      {error !== null && (
        <p className="mt-2 text-[10px] text-destructive" data-testid="review-apply-error">
          {error}
        </p>
      )}
      {done !== null && (
        <p className="mt-2 text-[10px] text-success" data-testid="review-apply-done">
          {done}
        </p>
      )}
    </Shell>
  )
}

function Shell({
  tone,
  children,
}: {
  tone: 'warning' | 'muted'
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <section
      data-testid="review-panel"
      className={cn(
        'qbar-card p-3',
        tone === 'warning' && 'border-warning/40 bg-warning/[0.04]',
      )}
    >
      {children}
    </section>
  )
}

function Tag({
  tone,
  title,
  children,
}: {
  tone: 'warning' | 'destructive' | 'muted'
  title: string
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <span
      title={title}
      className={cn(
        'ml-1 rounded px-1 py-0.5 text-[8px] font-extrabold',
        tone === 'warning' && 'bg-warning/15 text-warning',
        tone === 'destructive' && 'bg-destructive/15 text-destructive',
        tone === 'muted' && 'bg-muted text-muted-foreground',
      )}
    >
      {children}
    </span>
  )
}

function Choice({
  on,
  disabled,
  title,
  testId,
  onClick,
  children,
}: {
  on: boolean
  disabled?: boolean
  title: string
  testId: string
  onClick: () => void
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <button
      type="button"
      title={title}
      data-testid={testId}
      aria-pressed={on}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'h-7 rounded-md px-2 text-[10px] font-extrabold transition-all disabled:opacity-30',
        on
          ? 'bg-foreground text-background'
          : 'border border-border text-muted-foreground hover:bg-secondary hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}
